import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { TranslateService } from '@icc/shared';
import { APP_CONFIG, AppConfigFactory } from '@icc/shared';
import { core } from '@icc/shared/helpers';
import { isNotNullOrUndefined } from '@icc/shared/helpers';
import { getDefaultShapeDimensions } from '@icc/window-door/types';
import {
    ConfigurationsRegistry,
    Configurations,
    ConfigurationType,
    ConfiguratorEnvironment
} from '@icc/configurator/types';
import { ConfiguratorsDataService } from '@icc/shared';

export type ActiveConfigurationImpl = InstanceType<Configurations[ConfigurationType]['active']>;
export type ConfigurationImpl = InstanceType<Configurations[ConfigurationType]['simple']>;

@Injectable()
export abstract class ConfigurationsService<T extends ConfigurationType = ConfigurationType> {
    abstract conf: ConfiguratorEnvironment<T>;

    conf$: BehaviorSubject<Partial<ConfiguratorEnvironment<T>>> = new BehaviorSubject<
        Partial<ConfiguratorEnvironment<T>>
    >({});

    configuration$ = this.conf$.pipe(
        filter(isNotNullOrUndefined),
        map(conf => conf.Current),
        filter(isNotNullOrUndefined)
    );

    price: any = { noPrice: null };
    version = 1;
    configurators: Partial<Record<ConfigurationType, ConfiguratorEnvironment>> = {};
    systemsComparisonEdit = false;

    editedPosition: Partial<Record<ConfigurationType, {
        _id: string;
        clientPrice?: number;
        dealerPrice?: number;
    } | null>> = {};

    copiedPosition = false;

    constructor(
        protected translateService: TranslateService,
        @Inject(APP_CONFIG) protected config: AppConfigFactory
    ) {}

    init(
        startState?: InstanceType<Configurations[T]['active']> | InstanceType<Configurations[T]['simple']>,
        edit = false,
        editedPositionPrices = null,
    ) {}

    /**
     * Zwraca klasę Simple Configuration używając Registry
     */
    getSimpleConfiguration<K extends ConfigurationType>(type: K): Configurations[K]['simple'] | null {
        return ConfigurationsRegistry.getConfigurationClass(type) as Configurations[K]['simple'] | null;
    }

    /**
     * Zwraca klasę Active Configuration używając Registry
     */
    getActiveConfiguration<K extends ConfigurationType>(type: K): Configurations[K]['active'] | null {
        return ConfigurationsRegistry.getActiveConfigurationClass(type) as Configurations[K]['active'] | null;
    }

    /**
     * Tworzy Simple Configuration używając Registry
     */
    createSimpleConfiguration(
        configuration: any,
        dataRequiredForUpdate: any = {},
        configuratorsDataService?: ConfiguratorsDataService,
        langCode: string = this.config().CurLang,
        config = this.config()
    ): InstanceType<Configurations[ConfigurationType]['simple']> | null {
        const ConfigClass = ConfigurationsRegistry.getConfigurationClass(configuration.type);

        if (!ConfigClass) {
            console.error(`Configurator ${configuration.type} is not registered!`);
            return null;
        }

        return new ConfigClass(
            configuration,
            dataRequiredForUpdate,
            true,
            configuratorsDataService,
            langCode,
            config
        ) as InstanceType<Configurations[ConfigurationType]['simple']>;
    }

    /**
     * Tworzy ActiveConfiguration używając Registry
     */
    createActiveConfiguration(
        configuration?: any | null,
        configuratorsDataService?: ConfiguratorsDataService,
        langCode?: string,
        config = this.config(),
    ): InstanceType<Configurations[ConfigurationType]['active']> | null {
        if (configuration) {
            const ConfigClass = ConfigurationsRegistry.getActiveConfigurationClass(
                configuration.type
            );

            if (!ConfigClass) {
                console.error(`Configurator ${configuration.type} is not registered!`);
                return null;
            }

            return new ConfigClass(configuration, configuratorsDataService, langCode, config) as InstanceType<Configurations[ConfigurationType]['active']>;
        } else {
            const ConfigClass = ConfigurationsRegistry.getActiveConfigurationClass(
                this.conf.conf
            );

            if (!ConfigClass) {
                console.error(`Configurator ${this.conf.conf} is not registered!`);
                return null;
            }

            return this.setDefaults(new ConfigClass());
        }
    }

    /**
     * Type guard - sprawdza typ konfiguracji
     */
    isType<K extends ConfigurationType>(
        configuration: any,
        type: K
    ): configuration is InstanceType<Configurations[K]['active']> {
        return configuration?.type === type;
    }

    setDefaults(configuration: any, newConfiguration = true): any {
        switch (configuration.type) {
            case 'window':
                if (newConfiguration) {
                    configuration.Width = this.config().IccConfig.Configurators?.window?.dimensions?.width || 1500;
                    configuration.Height = this.config().IccConfig.Configurators?.window?.dimensions?.height || 1500;
                    configuration.Shape = getDefaultShapeDimensions('rect', configuration.Width, configuration.Height);
                }
                configuration.Steel = this.config().IccConfig.Configurators.steelSelect
                    ? 'Opened'
                    : null;
                configuration.DrawId = 'draw_' + core.generateUUID();
                configuration.RollerShutter.hasAssembly = this.config().IccConfig.Configurators.roller_shutter.defaultAssemblyToWindow;
                break;
            case 'hs':
                if (newConfiguration) {
                    configuration.Width = this.config().IccConfig.Configurators?.hs?.dimensions?.width || 2000;
                    configuration.Height = this.config().IccConfig.Configurators?.hs?.dimensions?.height || 2000;
                    configuration.Shape = getDefaultShapeDimensions('rect', configuration.Width, configuration.Height);
                }
                configuration.Steel = this.config().IccConfig.Configurators.steelSelect
                    ? 'Opened'
                    : null;
                configuration.DrawId = 'draw_' + core.generateUUID();
                configuration.RollerShutter.hasAssembly = this.config().IccConfig.Configurators.roller_shutter.defaultAssemblyToWindow;
                break;
            case 'sliding_door':
                if (newConfiguration) {
                    configuration.Width = this.config().IccConfig.Configurators?.sliding_door?.dimensions?.width || 2000;
                    configuration.Height = this.config().IccConfig.Configurators?.sliding_door?.dimensions?.height || 2000;
                    configuration.Shape = getDefaultShapeDimensions('rect', configuration.Width, configuration.Height);
                }
                configuration.Steel = this.config().IccConfig.Configurators.steelSelect
                    ? 'Opened'
                    : null;
                configuration.DrawId = 'draw_' + core.generateUUID();
                configuration.RollerShutter.hasAssembly = this.config().IccConfig.Configurators.roller_shutter.defaultAssemblyToWindow;
                break;
            case 'door':
                if (newConfiguration) {
                    configuration.Width = this.config().IccConfig.Configurators?.door?.dimensions?.width || 1000;
                    configuration.Height = this.config().IccConfig.Configurators?.door?.dimensions?.height || 2000;
                    configuration.Shape = getDefaultShapeDimensions('rect', configuration.Width, configuration.Height);
                    configuration.Quantity = this.config().quantity || 1;
                    configuration.Title = this.config().userDescription || '';
                }
                configuration.RollerShutter.hasAssembly = this.config().IccConfig.Configurators.roller_shutter.defaultAssemblyToWindow;
                break;
            case 'accessory':
                configuration.Name = this.translateService.instant('ACCESSORY|Akcesoria');
                break;
            case 'complementary_goods':
                configuration.Name = this.config().IccConfig.Configurators?.complementary_goods
                    .namedAccessory
                    ? this.translateService.instant('ACCESSORY|Akcesoria')
                    : this.translateService.instant('ACCESSORY|Dobra komplementarne');
                break;
            case 'coupled_window':
                configuration.Name = this.translateService.instant('COUPLED_WINDOW|Konstrukcje złożone');
                break;
            case 'roller_shutter':
                if (newConfiguration) {
                    configuration.Width = this.config().IccConfig.Configurators?.roller_shutter?.dimensions?.width || 1500;
                    configuration.Height = this.config().IccConfig.Configurators?.roller_shutter?.dimensions?.height || 1500;
                }

                if (configuration?.RollerShutter?.Accessories) {
                    configuration.Accessories = [...configuration?.RollerShutter?.Accessories];
                }

                break;
        }
        return configuration;
    }

    updatedConfiguration() {
        this.conf$.next(this.conf);
    }

    setConstructionName(name: string) {
        this.conf.Current.Title = name;
        this.updatedConfiguration();
    }

    getConstructionName() {
        return this.conf.Current.Title || '';
    }

    setQuantity(quantity = 1) {
        if (!this.conf?.Current) {
            return;
        }

        if (quantity > 0) {
            this.conf.Current.Quantity = quantity;
            this.updatedConfiguration();
        } else {
            this.conf.Current.Quantity = 1;
        }
    }

    getQuantity() {
        return this.conf.Current.Quantity;
    }

    abstract getOrInitConfiguratorConfigurations<U extends keyof Configurations>(
        type: U,
        startState?:
            | InstanceType<Configurations[U]['active']>
            | InstanceType<Configurations[U]['simple']>
    ): ConfiguratorEnvironment<U>;
}
